import ConfirmationDialog from 'components/ReusableComponents/ConfirmationDialog'
import CreditCardIcon from 'components/ReusableComponents/CreditCardIcon'
import AddIcon from 'components/ReusableComponents/Icons/Add'
import ExclamationIcon from 'components/ReusableComponents/Icons/Exclamation'
import Toggle from 'components/ReusableComponents/Toggle'
import HistoryIcon from 'features/Billing/components/PaymentMethodStatus/history.svg'
import { useState } from 'react'

import { ReactComponent as ACHIcon } from './ACH.svg'
// accountHolderName
// :
// "Celebration PH Holdings LTD"
// bankName
// :
// "BANK OF NEW YORK MELLON"
// friendlyDisplayName
// :
// "BANK OF NEW YORK MELLON-5167"
// isDefaultAccount
// :
// false
// last4
// :
// "5167"
// method
// :
// "Check"
// network
// :
// "US"
// paymentAccountId
// :
// 436
import styles from './PaymentMethodsDialog.module.scss'

const PaymentMethodCard = ({
  isDefaultAccount,
  last4,
  method,
  network,
  paymentAccountId,
  friendlyDisplayName,
  isExpired,
  isDisabled,
  expirationDate,
  isStatusChanging,
  onSelect,
  onClickMakeDefault,
  onClickDelete,
  onClickEdit,
  onClickHistory,
  onToggleStatus
}) => {
  const [isDeleteConfirmationVisible, setIsDeleteConfirmationVisible] = useState(false)

  if (isDeleteConfirmationVisible) {
    return (
      <ConfirmationDialog
        title={`Do you really want to remove ${network} ending in ${last4}`}
        hasDangerousAction
        positiveMessage="Yes, remove this card"
        negativeMessage="Go back"
        onClose={() => setIsDeleteConfirmationVisible(false)}
        onConfirm={() => onClickDelete()}
      >
        <p>This action cannot be undone.</p>
      </ConfirmationDialog>
    )
  }

  return (
    <>
      <div
        className={[
          isExpired ? styles.card : styles.cardClickable,
          isDisabled ? styles.cardDisabled : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <button
          className={styles.cardClickArea}
          onClick={isExpired ? () => null : onSelect}
        >
          <div className={styles.icon}>
            {method === 'CreditCard' && <CreditCardIcon type={network} />}
            {method === 'Check' && <ACHIcon className={styles.achIcon} />}
            {method === 'ACH' && <ACHIcon className={styles.achIcon} />}
          </div>
          <div className={styles.paymentMethodTexts}>
            <div className={styles.primaryText}>
              {method === 'CreditCard' && (
                <>
                  <strong>{network}</strong> ending in {last4}
                </>
              )}
              {method === 'Check' && (
                <>
                  <strong>Check</strong>
                </>
              )}
              {method === 'ACH' && (
                <>
                  <strong>ACH</strong>
                </>
              )}
            </div>
            <div className={styles.secondaryText}>
              {method === 'CreditCard' && !isExpired && <>Expires {expirationDate}</>}
              {method === 'CreditCard' && isExpired && (
                <span className={styles.expiredText}>
                  <ExclamationIcon /> Expired
                </span>
              )}
              {method === 'Check' && <>{friendlyDisplayName}</>}
              {method === 'ACH' && <>{friendlyDisplayName}</>}
              {isDisabled && isDefaultAccount && (
                <div className={styles.disabledWarning}>
                  Autocharge will not run until enabled or another method is default.
                </div>
              )}
              {isDisabled && !isDefaultAccount && (
                <div className={styles.disabledWarning}>
                  Disabled for auto-charge; available for manual payment.
                </div>
              )}
            </div>
          </div>
        </button>
        <div className={styles.controls}>
          {isDefaultAccount && !isExpired && (
            <p className={styles.defaultMethodText}>Default method</p>
          )}
          {isExpired && (
            <>
              <button className={styles.control} onClick={onClickEdit}>
                Update
              </button>
              <button
                className={styles.control}
                onClick={() => setIsDeleteConfirmationVisible(true)}
              >
                Remove
              </button>
            </>
          )}
          {!isExpired && !isDisabled && !isDefaultAccount && (
            <button className={styles.control} onClick={onClickMakeDefault}>
              Make default
            </button>
          )}
          <div className={styles.statusControls}>
            <Toggle
              id={`saved-payment-method-${paymentAccountId}`}
              label="Allow automatic charges with this payment method"
              labelClass={styles.toggleLabel}
              checked={!isDisabled}
              disabled={isStatusChanging}
              handleChange={onToggleStatus}
              mini
            />
            <button
              type="button"
              className={styles.historyButton}
              onClick={onClickHistory}
              aria-label="View payment method disable history"
            >
              <img src={HistoryIcon} alt="" />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

const PaymentMethodsDialog = ({
  savedPaymentMethods,
  onSelect,
  onClose,
  makeMethodDefault,
  deleteMethod,
  setCardIdToEdit,
  savingPaymentAccountId,
  openStatusDialog,
  openHistoryDialog
}) => (
  <ConfirmationDialog
    title="Use a saved payment method"
    theme="blacklist"
    maxWidth={540}
    onClose={onClose}
    hasConfirmButton={false}
  >
    <div className={styles.cards}>
      {savedPaymentMethods.map(m => (
        <PaymentMethodCard
          key={m.paymentAccountId}
          accountHolderName={m.accountHolderName}
          bankName={m.bankName}
          isDefaultAccount={m.isDefaultAccount}
          last4={m.last4}
          method={m.method}
          network={m.network}
          paymentAccountId={m.paymentAccountId}
          friendlyDisplayName={m.friendlyDisplayName}
          onSelect={() => onSelect(m.paymentAccountId)}
          isExpired={m.isExpired}
          isDisabled={m.isDisabled}
          expirationDate={m.expirationDate}
          isStatusChanging={Boolean(savingPaymentAccountId)}
          onClickMakeDefault={() => makeMethodDefault(m.paymentAccountId)}
          onClickDelete={() => deleteMethod(m.paymentAccountId)}
          onClickEdit={() => setCardIdToEdit(m.paymentAccountId)}
          onClickHistory={() => openHistoryDialog(m.paymentAccountId)}
          onToggleStatus={() => openStatusDialog(m)}
        />
      ))}
    </div>
    <button className={styles.newMethodButton} onClick={onClose}>
      <AddIcon />
      Add a new credit card or ACH
    </button>
  </ConfirmationDialog>
)

export default PaymentMethodsDialog
